/**
 * Derived views of catalogs for tables, charts, and talent profiles.
 * Components should import these from `content`, not from catalog files.
 */
import { getActiveLocale } from './runtime';
import { dictionaries } from './store';
import { order as competencyOrder } from './catalogs/competencies';
import { order as motivationOrder } from './catalogs/motivations';
import { order as personalityOrder } from './catalogs/personality';
import { order as teamStrengthOrder } from './catalogs/teamStrengths';
import { order as salesStrengthOrder } from './catalogs/salesStrengths';
import { aspectOrder, styleOrder } from './catalogs/adept';
import { aliases as reportNameAliases } from './catalogs/reports';
import { EMPLOYEE_COUNT_VALUES } from '../constants';

function camelFromSnake(value) {
  return String(value).replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

function snakeFromCamel(value) {
  return String(value).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

function addLookupKeys(map, key, value) {
  if (key == null || value == null) return;
  const asString = String(key);
  map[asString] = value;
  map[camelFromSnake(asString)] = value;
  map[snakeFromCamel(asString)] = value;
}

function enItemFor(enCatalog, item) {
  if (!enCatalog || !item) return null;
  return enCatalog[String(item.id)] || enCatalog[item.id] || enCatalog[item.slug] || null;
}

function addCatalogLookupKeys(map, item, enCatalog, value) {
  addLookupKeys(map, item.slug, value);
  addLookupKeys(map, item.id, value);
  addLookupKeys(map, item.title, value);
  const enItem = enItemFor(enCatalog, item);
  if (enItem?.title && enItem.title !== item.title) {
    addLookupKeys(map, enItem.title, value);
  }
}

function normalizeKey(value) {
  return String(value).toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]/g, '');
}

function titleMap(items, enCatalog) {
  const map = {};
  items.forEach((item) => {
    if (!item?.title) return;
    addCatalogLookupKeys(map, item, enCatalog, item.title);
    map[normalizeKey(item.title)] = item.title;
    const enItem = enItemFor(enCatalog, item);
    if (enItem?.title) map[normalizeKey(enItem.title)] = item.title;
  });
  return map;
}

function tooltipMap(items, enCatalog, textOf) {
  const map = {};
  items.forEach((item) => {
    const text = textOf(item);
    if (!text) return;
    addCatalogLookupKeys(map, item, enCatalog, text);
  });
  return map;
}

function scaleDescriptionMap(items, enCatalog, entryOf) {
  const map = {};
  items.forEach((item) => {
    addCatalogLookupKeys(map, item, enCatalog, entryOf(item));
  });
  return map;
}

function dictFor(locale) {
  return dictionaries[locale] || dictionaries.en || {};
}

export function buildLookups(locale = 'en') {
  const dict = dictFor(locale);
  const enDict = dictFor('en');
  const competencies = dict.catalogs?.competencies || {};
  const motivations = dict.catalogs?.motivations || {};
  const personality = dict.catalogs?.personality || {};
  const teamStrengths = dict.catalogs?.teamStrengths || {};
  const salesStrengths = dict.catalogs?.salesStrengths || {};
  const adept = dict.catalogs?.adept || { aspects: {}, styles: {} };
  const assessments = dict.catalogs?.assessments || {};
  const reports = dict.catalogs?.reports || {};
  const industries = dict.catalogs?.industries || {};
  const profiles = dict.catalogs?.profiles || {};
  const help = dict.help || {};
  const common = dict.common || {};
  const enCompetencies = enDict.catalogs?.competencies || {};
  const enMotivations = enDict.catalogs?.motivations || {};
  const enPersonality = enDict.catalogs?.personality || {};
  const enTeamStrengths = enDict.catalogs?.teamStrengths || {};
  const enSalesStrengths = enDict.catalogs?.salesStrengths || {};
  const enAdept = enDict.catalogs?.adept || { aspects: {}, styles: {} };

  const competencyItems = competencyOrder
    .map((id) => competencies[String(id)])
    .filter(Boolean);
  const motivationItems = motivationOrder
    .map((id) => motivations[String(id)])
    .filter(Boolean);
  const personalityItems = personalityOrder
    .map((id) => personality[id])
    .filter(Boolean);
  const teamStrengthItems = teamStrengthOrder
    .map((id) => teamStrengths[id])
    .filter(Boolean);
  const salesStrengthItems = salesStrengthOrder
    .map((id) => salesStrengths[id])
    .filter(Boolean);
  const adeptAspectItems = aspectOrder
    .map((id) => adept.aspects?.[id])
    .filter(Boolean);
  const adeptStyleItems = styleOrder
    .map((id) => adept.styles?.[id])
    .filter(Boolean);
  const industryItems = Object.values(industries)
    .filter((item) => item?.id != null && item.title)
    .sort(
      (a, b) =>
        Number(a.sortOrder != null ? a.sortOrder : a.id) -
        Number(b.sortOrder != null ? b.sortOrder : b.id)
    );
  const industryTitles = {};
  industryItems.forEach((item) => {
    industryTitles[String(item.id)] = item.title;
  });
  const profileItems = Object.values(profiles).filter(
    (item) => item?.id != null && item.title
  );
  const profileTitles = {};
  const profileTitlesByCode = {};
  profileItems.forEach((item) => {
    profileTitles[String(item.id)] = item.title;
    if (item.code) {
      profileTitlesByCode[String(item.code)] = item.title;
    }
  });

  const workingEnvironment = motivations['17'];
  const competencyTooltips = tooltipMap(competencyItems, enCompetencies, (item) => item.summary || item.description);
  const motivationTooltips = tooltipMap(motivationItems, enMotivations, (item) => item.summary);
  if (workingEnvironment?.summary) {
    addLookupKeys(motivationTooltips, 'last', workingEnvironment.summary);
  }
  const personalityTooltips = tooltipMap(personalityItems, enPersonality, (item) => item.summary);
  const teamStrengthTooltips = tooltipMap(teamStrengthItems, enTeamStrengths, (item) => item.summary);
  addLookupKeys(teamStrengthTooltips, 'implementation', teamStrengths.initiativeAndResponsibility?.summary);
  const salesStrengthTooltips = tooltipMap(salesStrengthItems, enSalesStrengths, (item) => item.summary);
  const adeptTooltips = tooltipMap(adeptAspectItems, enAdept.aspects || {}, (item) => item.definition);

  const scaleChrome = common.scale || {};
  const competencyScaleDescription = scaleDescriptionMap(competencyItems, enCompetencies, (item) => ({
    title: item.description || item.summary,
    properties: [
      { title: scaleChrome.howToLead, content: item.howToLead || [] },
      { title: scaleChrome.howToDevelop, content: item.howToDevelop || [] },
    ],
  }));

  const motivationScaleDescription = scaleDescriptionMap(motivationItems, enMotivations, (item) => ({
    high: item.high || [],
    low: item.low || [],
    properties: [
      { title: scaleChrome.howToManage, content: item.howToManage || [] },
      { title: scaleChrome.watchOut, content: item.watchOut || [] },
    ],
  }));
  if (workingEnvironment) {
    addLookupKeys(motivationScaleDescription, 'last', motivationScaleDescription[workingEnvironment.slug]);
  }

  const personalityScaleDescription = scaleDescriptionMap(personalityItems, enPersonality, (item) => ({
    title: item.summary,
    sample_items: item.sampleItems || [],
    high: item.high || [],
    low: item.low || [],
  }));

  const teamStrengthScaleDescription = scaleDescriptionMap(teamStrengthItems, enTeamStrengths, (item) => ({
    high: item.high || [],
    low: item.low || [],
  }));
  addLookupKeys(
    teamStrengthScaleDescription,
    'implementation',
    teamStrengthScaleDescription.initiativeAndResponsibility
  );

  const salesStrengthScaleDescription = scaleDescriptionMap(salesStrengthItems, enSalesStrengths, (item) => ({
    high: item.high || [],
    low: item.low || [],
  }));

  const adeptScaleDescription = scaleDescriptionMap(adeptAspectItems, enAdept.aspects || {}, (item) => ({
    high: item.high || [],
    low: item.low || [],
  }));

  const metricTitles = {
    ...titleMap(competencyItems, enCompetencies),
    ...titleMap(motivationItems, enMotivations),
    ...titleMap(personalityItems, enPersonality),
    ...titleMap(teamStrengthItems, enTeamStrengths),
    ...titleMap(salesStrengthItems, enSalesStrengths),
    ...titleMap(adeptAspectItems, enAdept.aspects || {}),
    ...titleMap(adeptStyleItems, enAdept.styles || {}),
  };
  if (workingEnvironment?.title) {
    addLookupKeys(metricTitles, 'last', workingEnvironment.title);
    addLookupKeys(metricTitles, 'working_environment', workingEnvironment.title);
    metricTitles[normalizeKey(workingEnvironment.title)] = workingEnvironment.title;
  }
  if (teamStrengths.initiativeAndResponsibility?.title) {
    addLookupKeys(metricTitles, 'implementation', teamStrengths.initiativeAndResponsibility.title);
  }

  const reportLanguages = {};
  Object.values(reports).forEach((report) => {
    reportLanguages[String(report.id)] = {
      name: report.name,
      languages: report.languages,
      report_id: report.id,
      instrument_id: report.instrumentId,
    };
  });

  return {
    competencyTooltips,
    competencyScaleDescription,
    competencyList: competencyItems.map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description,
    })),
    motivationTooltips,
    motivationScaleDescription,
    motivationList: motivationItems.map((item) => ({
      id: item.id,
      title: item.title,
      description_yes: item.highDescription,
      description_no: item.lowDescription,
    })),
    personalityTooltips,
    personalityScaleDescription,
    teamStrengthTooltips,
    teamStrengthScaleDescription,
    teamStrengthLabels: teamStrengthItems.map((item) => item.title),
    salesStrengthTooltips,
    salesStrengthScaleDescription,
    salesStrengthLabels: salesStrengthItems.map((item) => item.title),
    adeptTooltips,
    adeptScaleDescription,
    adeptAspectLabels: adeptAspectItems.map((item) => item.title),
    adeptWorkStyleLabels: adeptStyleItems.map((item) => item.title),
    metricTitles,
    assessments,
    reasoningAndSkills: [302, 344, 301, 370]
      .map((id) => assessments[String(id)])
      .filter(Boolean)
      .map((item) => ({
        id: item.id,
        label: item.name,
        description: item.profileDescription,
      })),
    reportNames: dict.reportAliases || reportNameAliases[locale] || reportNameAliases.en || reportNameAliases,
    reportLanguages,
    faqServiceUse: help.serviceUse,
    faqAnalytics: help.analytics,
    employeeSizeOptions: EMPLOYEE_COUNT_VALUES.map((value) => ({
      value,
      title: (common.employeeSizeLabels && common.employeeSizeLabels[value]) || value,
    })),
    industryTitles,
    profileTitles,
    profileTitlesByCode,
  };
}

export function getIndustryTitle(industry, locale) {
  if (industry == null || industry === '') return '';
  const id = typeof industry === 'object' ? industry.id : industry;
  if (id != null && id !== '') {
    const byId = getLookups(locale).industryTitles?.[String(id)];
    if (byId) return byId;
  }
  if (typeof industry === 'object') {
    return industry.title || industry.name || '';
  }
  return '';
}

export function localizeIndustryList(list, locale) {
  return (list || [])
    .map((item) => {
      const title = getIndustryTitle(item, locale);
      if (!title) return null;
      return { ...item, title, value: item.id };
    })
    .filter(Boolean);
}

export function getProfileTitle(profile, locale) {
  if (profile == null || profile === '') return '';
  const lookupsForLocale = getLookups(locale);
  const id = typeof profile === 'object' ? profile.id : profile;
  if (id != null && id !== '') {
    const byId = lookupsForLocale.profileTitles?.[String(id)];
    if (byId) return byId;
  }
  const code = typeof profile === 'object' ? profile.code : null;
  if (code) {
    const byCode = lookupsForLocale.profileTitlesByCode?.[String(code)];
    if (byCode) return byCode;
  }
  if (typeof profile === 'object') {
    return profile.title || profile.name || '';
  }
  return '';
}

export function localizeProfileList(list, locale) {
  return (list || []).map((item) => ({
    ...item,
    title: getProfileTitle(item, locale) || item.title || item.name || '',
    value: item.id,
  }));
}

const lookupsByLocale = {};

export function clearLookupsCache(locale) {
  if (locale) {
    delete lookupsByLocale[locale];
    return;
  }
  Object.keys(lookupsByLocale).forEach((key) => {
    delete lookupsByLocale[key];
  });
}

export function getLookups(locale) {
  const loc = locale || getActiveLocale();
  if (!lookupsByLocale[loc]) {
    lookupsByLocale[loc] = buildLookups(loc);
  }
  return lookupsByLocale[loc];
}

export const lookups = new Proxy(
  {},
  {
    get(_target, prop) {
      if (typeof prop === 'symbol' || prop === 'then') return undefined;
      return getLookups()[prop];
    },
    has(_target, prop) {
      return typeof prop === 'string' && prop in getLookups();
    },
    ownKeys() {
      return Reflect.ownKeys(getLookups());
    },
    getOwnPropertyDescriptor(_target, prop) {
      const table = getLookups();
      if (!(prop in table)) return undefined;
      return { configurable: true, enumerable: true, writable: false, value: table[prop] };
    },
  }
);
